import apiClient from './client';
import {
    AuditLog,
    CampaignStatus,
    PaginatedResponse,
    RevenueReport,
    SystemSettings,
    User,
    UserRole,
} from '../types/campaign.types';

export const adminApi = {
    getSettings: async (): Promise<SystemSettings> => {
        const response = await apiClient.get<SystemSettings>('/api/admin/settings');
        return response.data;
    },
    updateSettings: async (data: {
        currency: string;
        amount_per_sms: number;
        workmanship_fee: number;
        sender_id_price: number;
    }): Promise<SystemSettings> => {
        const response = await apiClient.put<SystemSettings>('/api/admin/settings', data);
        return response.data;
    },
    listPricingPlans: async (): Promise<SystemSettings[]> => {
        const response = await apiClient.get<SystemSettings[]>('/api/admin/pricing-plans');
        return response.data;
    },
    createPricingPlan: async (data: {
        currency: string;
        amount_per_sms: number;
        workmanship_fee: number;
        sender_id_price: number;
        is_active?: boolean;
    }): Promise<SystemSettings> => {
        const response = await apiClient.post<SystemSettings>('/api/admin/pricing-plans', data);
        return response.data;
    },
    activatePricingPlan: async (planId: number): Promise<SystemSettings> => {
        const response = await apiClient.patch<SystemSettings>(
            `/api/admin/pricing-plans/${planId}/activate`
        );
        return response.data;
    },
    createUser: async (data: {
        email: string;
        full_name: string;
        password: string;
        role: UserRole;
    }): Promise<User> => {
        const response = await apiClient.post<User>('/api/admin/users', data);
        return response.data;
    },
    listUsers: async (): Promise<User[]> => {
        const response = await apiClient.get<User[]>('/api/admin/users');
        return response.data;
    },
    updateUser: async (userId: number, data: {
        full_name?: string;
        role?: UserRole;
        is_active?: boolean;
    }): Promise<User> => {
        const response = await apiClient.patch<User>(`/api/admin/users/${userId}`, data);
        return response.data;
    },
    getAuditLogs: async (params: {
        page?: number;
        page_size?: number;
        action?: string;
        user_email?: string;
        start_date?: string;
        end_date?: string;
    }): Promise<PaginatedResponse<AuditLog>> => {
        const response = await apiClient.get<PaginatedResponse<AuditLog>>('/api/admin/audit-logs', { params });
        return response.data;
    },
    getRevenueReport: async (params?: {
        start_date?: string;
        end_date?: string;
    }): Promise<RevenueReport> => {
        const response = await apiClient.get<RevenueReport>('/api/admin/reports/revenue', { params });
        return response.data;
    },
    exportRevenueReport: async (params?: {
        start_date?: string;
        end_date?: string;
        fields?: string[];
        status_filter?: CampaignStatus[];
    }) => {
        const response = await apiClient.get('/api/admin/reports/revenue', {
            params: {
                start_date: params?.start_date,
                end_date: params?.end_date,
                fields: params?.fields?.join(','),
                status_filter: params?.status_filter?.join(','),
                export: 'csv',
            },
            responseType: 'blob',
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'revenue-report.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    },
};

export default adminApi;
